import { useQuery, keepPreviousData } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import type {
  AuditActorCandidatesData,
  AuditEventsData,
} from "@/hooks/queries/admin-audit-types";

// GET /admin/audit (Story 11.7, AC9) — the super_admin audit console read.
// TanStack Query v5 through `apiFetch`; no manual fetch/useEffect.

/** Root key for every audit-log query — `invalidateQueries([AUDIT_LOG_QUERY_KEY])`. */
export const AUDIT_LOG_QUERY_KEY = "admin-audit-log";

/** Root key for the actor-search lookups the filter bar drives. */
export const AUDIT_ACTOR_SEARCH_QUERY_KEY = "admin-audit-actor-search";

/** Backend default page size for this endpoint (AC2: 25, ge=1, le=100). */
export const AUDIT_LOG_PAGE_SIZE = 25;

/**
 * How many actor candidates the picker asks for. A filter picker is scanned, not
 * paged: past ~10 rows an operator refines the term instead of scrolling, and the
 * list would cover the table beneath it. No named source — see the Dev Agent Record.
 */
export const ACTOR_CANDIDATE_LIMIT = 10;

export interface AuditLogFilters {
  /** 1-indexed page. */
  page: number;
  /** Actor UUID — resolved by the picker; the raw e-mail is never sent here. */
  actor_id?: string;
  event_name?: string;
  org_id?: string;
  /** ISO instant (the FE converts the picked local date to an instant). */
  since?: string;
  until?: string;
}

/** Build the query string, omitting every unset filter so page 2 is never narrowed. */
export function buildAuditLogPath(filters: AuditLogFilters): string {
  const params = new URLSearchParams();
  params.set("page", String(filters.page));
  params.set("limit", String(AUDIT_LOG_PAGE_SIZE));
  if (filters.actor_id) params.set("actor_id", filters.actor_id);
  if (filters.event_name) params.set("event_name", filters.event_name);
  if (filters.org_id) params.set("org_id", filters.org_id);
  if (filters.since) params.set("since", filters.since);
  if (filters.until) params.set("until", filters.until);
  return `/admin/audit?${params.toString()}`;
}

export function useQueryAuditLog(filters: AuditLogFilters) {
  return useQuery({
    queryKey: [AUDIT_LOG_QUERY_KEY, filters] as const,
    queryFn: () => apiFetch<AuditEventsData>(buildAuditLogPath(filters)),
    // Keep the current page visible while the next loads, so paging never flashes
    // the full-page loading block (the evaluations-list posture).
    placeholderData: keepPreviousData,
  });
}

/**
 * Actor lookup for the filter bar — `GET /admin/users?q=` (Story 11.1 AC8).
 *
 * This is the whole reason the audit endpoint accepts no e-mail parameter: the
 * address is resolved to a UUID HERE, and only the UUID reaches `/admin/audit`,
 * its meta-audit payload and its logs. Disabled below the minimum term length so
 * a single keystroke does not ask the server for the entire directory.
 */
export function useAuditActorSearch(term: string, minChars: number) {
  const trimmed = term.trim();
  const enabled = trimmed.length >= minChars;
  return useQuery({
    queryKey: [AUDIT_ACTOR_SEARCH_QUERY_KEY, trimmed] as const,
    queryFn: () =>
      apiFetch<AuditActorCandidatesData>(
        `/admin/users?q=${encodeURIComponent(trimmed)}&limit=${ACTOR_CANDIDATE_LIMIT}`,
      ),
    enabled,
  });
}
